import React, { useEffect, useState } from "react";
import { getAllSessionLinks, toggleSessionLink, deleteSessionLink } from "../services/sessionLinkService";
import { getAllTimetableDays } from "../services/adminTimeTableServices";
import CreateLinkModal from "./CreateLinkModal";
import LinkRegistrationsDrawer from "./LinkRegistrationsDrawer";
import "./sessionLinks.css";

const AdminSessionLinksPage = () => {
  const [links, setLinks] = useState([]);
  const [sessionMap, setSessionMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedLink, setSelectedLink] = useState(null);

  const buildSessionMap = (days) => {
    const map = {};
    days.forEach((day) =>
      (day.sessions || []).forEach((s) => {
        map[s.id] = s;
      })
    );
    return map;
  };

  const loadAll = async () => {
    setLoading(true);
    const [linksRes, daysRes] = await Promise.all([
      getAllSessionLinks(),
      getAllTimetableDays(),
    ]);
    if (linksRes.status === 200) setLinks(linksRes.data);
    if (daysRes.data) setSessionMap(buildSessionMap(daysRes.data));
    setLoading(false);
  };

  useEffect(() => {
    loadAll();
  }, []);

  const handleToggle = async (id) => {
    const result = await toggleSessionLink(id);
    if (result.status === 200) {
      setLinks((prev) => prev.map((l) => (l.id === id ? result.data : l)));
    } else {
      alert(result.message || "Failed to toggle link.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this link? This cannot be undone.")) return;
    const result = await deleteSessionLink(id);
    if (result.status === 204 || result.status === 200) {
      setLinks((prev) => prev.filter((l) => l.id !== id));
    } else {
      alert(result.message || "Failed to delete link.");
    }
  };

  const handleCopy = (url) => {
    navigator.clipboard.writeText(url);
    alert("Link copied to clipboard!");
  };

  const handleCreated = (newLink) => {
    setLinks((prev) => [newLink, ...prev]);
    setShowCreateModal(false);
  };

  const formatDate = (iso) =>
    iso
      ? new Date(iso).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "—";

  const formatSessionCell = (sessionId) => {
    const s = sessionMap[sessionId];
    if (!s) return <span style={{ color: "#9ca3af" }}>ID: {sessionId}</span>;
    const time = s.startTime
      ? new Date(s.startTime).toLocaleString("en-IN", {
          timeZone: "Asia/Kolkata",
          day: "2-digit",
          month: "short",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
      : "";
    return (
      <div>
        <div style={{ fontWeight: 600, color: "#0f172a", fontSize: 14 }}>{s.topic}</div>
        <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>{time} IST</div>
      </div>
    );
  };

  return (
    <div className="session-links-page">
      <div className="sl-header">
        <h2>🔗 Session Public Links</h2>
        <button className="sl-btn-primary" onClick={() => setShowCreateModal(true)}>
          ➕ Create Link
        </button>
      </div>

      {loading ? (
        <p style={{ color: "#6b7280", padding: "20px" }}>Loading links...</p>
      ) : (
        <table className="sl-table">
          <thead>
            <tr>
              <th>Session</th>
              <th>Source</th>
              <th>Slug / Token</th>
              <th>Public URL</th>
              <th>Status</th>
              <th>Registrations</th>
              <th>Expires At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {links.length === 0 ? (
              <tr>
                <td colSpan="8">
                  <div className="sl-empty">
                    <span className="sl-empty-icon">🔗</span>
                    No session links yet. Create one to get started.
                  </div>
                </td>
              </tr>
            ) : (
              links.map((link) => (
                <tr key={link.id}>
                  <td>{formatSessionCell(link.sessionId)}</td>
                  <td>
                    {link.sourceLabel ? (
                      <span className="sl-source">{link.sourceLabel}</span>
                    ) : (
                      <span style={{ color: "#9ca3af" }}>—</span>
                    )}
                  </td>
                  <td style={{ fontFamily: "monospace", fontSize: 12 }}>
                    {link.slug || link.token?.slice(0, 16) + "..."}
                  </td>
                  <td>
                    <div className="sl-url-row">
                      <span className="sl-url-text" title={link.publicUrl}>
                        {link.publicUrl}
                      </span>
                      <button
                        className="sl-copy-btn"
                        onClick={() => handleCopy(link.publicUrl)}
                        title="Copy link"
                      >
                        📋 Copy
                      </button>
                    </div>
                  </td>
                  <td>
                    <span className={`sl-badge ${link.isActive ? "active" : "inactive"}`}>
                      {link.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td style={{ textAlign: "center", fontWeight: 600 }}>
                    {link.totalRegistrations ?? 0}
                  </td>
                  <td>{formatDate(link.expiresAt)}</td>
                  <td>
                    <div className="sl-actions">
                      <button
                        className="sl-action-btn toggle"
                        onClick={() => handleToggle(link.id)}
                        title={link.isActive ? "Deactivate" : "Activate"}
                      >
                        {link.isActive ? "⏸" : "▶️"}
                      </button>
                      <button
                        className="sl-action-btn view"
                        onClick={() => setSelectedLink(link)}
                        title="View registrations"
                      >
                        👥
                      </button>
                      <button
                        className="sl-copy-btn"
                        onClick={() => handleCopy(link.publicUrl)}
                        title="Copy link"
                        style={{ width: "auto", height: "auto" }}
                      >
                        📋
                      </button>
                      <button
                        className="sl-action-btn delete"
                        onClick={() => handleDelete(link.id)}
                        title="Delete link"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}

      {showCreateModal && (
        <CreateLinkModal
          onClose={() => setShowCreateModal(false)}
          onCreated={handleCreated}
        />
      )}

      {selectedLink && (
        <LinkRegistrationsDrawer
          link={selectedLink}
          onClose={() => setSelectedLink(null)}
        />
      )}
    </div>
  );
};

export default AdminSessionLinksPage;
